import { useMemo } from "react"
import { useAuth } from "@/lib/auth/auth-context"
import { useTeamRole } from "./use-current-role"
import { Roles, type Role } from "./roles"

// Single source of truth for "what is the active user allowed to do on a
// given team?" Every settings panel and member-action menu in the app
// should funnel through this hook so role-gating doesn't drift across
// surfaces (a button hidden in one place but rendered in another is the
// classic permission bug).
//
// The hook reads the user's per-team membership role via useTeamRole(teamId)
// — the SAME source the BE uses to authorize requests. When teamId is null
// or undefined the hook still runs (rules of hooks) but returns the
// read-only result.
//
// Why FE-side: the BE already enforces all of this server-side. The hook
// just hides UI the user can't use, so they don't see disabled controls.
// If a component wants to respect a server 403 (e.g. on a stale token),
// it should still surface the error — this hook is for proactive UX.

export interface TeamPermissions {
  canInvite: boolean
  canManageMembers: boolean
  canManageOrg: boolean
  isReadOnly: boolean
}

// Director (group owner) and GeneralManager (dealership / bodyshop owner)
// match the per-team-type owner enforcement in the BE's createInvite and
// removeMember handlers. Matches OWNER_ROLE_FOR_TYPE in roles.ts.
const MANAGE_TIER_ROLES: ReadonlySet<Role> = new Set([
  Roles.Director,
  Roles.GeneralManager,
])

// Adding/removing dealerships and connecting/disconnecting body shops only
// makes sense on a group, so only Director qualifies.
const ORG_TIER_ROLES: ReadonlySet<Role> = new Set([Roles.Director])

const FULL: TeamPermissions = {
  canInvite: true,
  canManageMembers: true,
  canManageOrg: true,
  isReadOnly: false,
}

const NONE: TeamPermissions = {
  canInvite: false,
  canManageMembers: false,
  canManageOrg: false,
  isReadOnly: true,
}

export function useTeamPermissions(teamId: number | null | undefined): TeamPermissions {
  const { user } = useAuth()
  const role = useTeamRole(teamId)

  return useMemo(() => {
    if (user?.admin) return FULL
    if (role === null) return NONE
    const canManageMembers = MANAGE_TIER_ROLES.has(role)
    const canManageOrg = ORG_TIER_ROLES.has(role)
    return {
      canInvite: canManageMembers,
      canManageMembers,
      canManageOrg,
      isReadOnly: !canManageMembers && !canManageOrg,
    }
  }, [user?.admin, role])
}
